import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';

// Mapbox access token
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';

if (typeof mapboxgl !== 'undefined') {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

const CAPTURE_RENDER_TIMEOUT = 5000;
export const DEFAULT_MAP_CAPTURE_WIDTH = 1536;
export const DEFAULT_MAP_CAPTURE_HEIGHT = 1024;

const ROUTE_LAYER_ID = 'route';
const VISIBILITY_PROBE_LAYER_ID = 'route-visibility-probe';
const VISIBILITY_PROBE_COLOR = { r: 255, g: 0, b: 255 };
const VISIBILITY_PROBE_COLOR_DISTANCE_THRESHOLD = 220;
const VISIBILITY_PROBE_MIN_STRENGTH = 90;
const SCREEN_DIAGONAL_BEARING_OFFSET = 45;
const MIN_CAMERA_SEARCH_PITCH = 32;
const MAX_CAMERA_SEARCH_PITCH = 70;
const DEFAULT_TERRAIN_EXAGGERATION = 1.5;
const ROUTE_LINE_OPACITY = 0.8;
const CAMERA_SEARCH_CACHE = new Map();
const DEFAULT_COVERAGE_REWARD_MULTIPLIER = 5;
const DEFAULT_FOOTPRINT_REWARD_MULTIPLIER = 1;
const DEFAULT_COARSE_BEARING_COUNT = 18;
// Camera-search tuning lives here.
const CAMERA_SEARCH_PITCHES = [60];
const PRIMARY_CAMERA_SEARCH_PITCH = CAMERA_SEARCH_PITCHES[0] ?? 60;
const REFINE_BEARING_OFFSETS = [];
const DEFAULT_ROBUST_CAMERA_RETRY_LIMIT = 3;
const TIGHTEN_ZOOM_STEP = 0.18;
const TIGHTEN_MAX_STEPS = 8;
const MAX_DEBUG_BREAK_MARKERS = 12;
const MAJOR_LABEL_LAYER_PATTERNS = [
    'country',
    'state',
    'province',
    'region',
    'settlement-major',
    'place-major',
    'capital'
];
const normalizeRouteLineWidth = (baseLineWidth) => Math.max(1, Number(baseLineWidth) || 1);

const normalizeBearing = (bearing) => ((bearing % 360) + 360) % 360;
const getBearingDelta = (fromBearing, toBearing) => {
    const delta = normalizeBearing(toBearing) - normalizeBearing(fromBearing);
    if (delta > 180) return delta - 360;
    if (delta < -180) return delta + 360;
    return delta;
};
const clampLatitude = (latitude) => Math.max(-85.051129, Math.min(85.051129, latitude));
const resolveCoarseBearingCount = (explicitCount) => {
    const parsedCount = Number(explicitCount);
    if (Number.isFinite(parsedCount)) {
        return Math.max(1, Math.round(parsedCount));
    }
    return DEFAULT_COARSE_BEARING_COUNT;
};
const getCoarseBearingStepDegrees = (coarseBearingCount) => 360 / Math.max(1, resolveCoarseBearingCount(coarseBearingCount));
const resolveRobustRetryLimit = (explicitLimit) => {
    const parsedLimit = Number(explicitLimit);
    if (Number.isFinite(parsedLimit)) {
        return Math.max(1, Math.min(10, Math.round(parsedLimit)));
    }
    return DEFAULT_ROBUST_CAMERA_RETRY_LIMIT;
};
const resolveCoverageRewardMultiplier = (explicitMultiplier) => {
    const parsedMultiplier = Number(explicitMultiplier);
    if (Number.isFinite(parsedMultiplier)) {
        return Math.max(0, parsedMultiplier);
    }
    return DEFAULT_COVERAGE_REWARD_MULTIPLIER;
};
const resolveFootprintRewardMultiplier = (explicitMultiplier) => {
    const parsedMultiplier = Number(explicitMultiplier);
    if (Number.isFinite(parsedMultiplier)) {
        return Math.max(0, parsedMultiplier);
    }
    return DEFAULT_FOOTPRINT_REWARD_MULTIPLIER;
};
const shouldKeepMajorLabelLayer = (layer) => {
    const combinedLayerName = `${layer?.id || ''} ${layer?.['source-layer'] || ''}`.toLowerCase();
    return MAJOR_LABEL_LAYER_PATTERNS.some((pattern) => combinedLayerName.includes(pattern));
};
const getUniqueSearchPitches = (basePitch) => {
    const normalizedPitches = new Set();
    return [basePitch, ...CAMERA_SEARCH_PITCHES]
        .map((candidatePitch) => clampPitch(candidatePitch))
        .filter((candidatePitch) => {
            const pitchKey = candidatePitch.toFixed(2);
            if (normalizedPitches.has(pitchKey)) return false;
            normalizedPitches.add(pitchKey);
            return true;
        });
};
const applyReducedLabelDensity = (mapRef, logPrefix = 'ActivityMap3D') => {
    const layers = mapRef?.getStyle?.()?.layers || [];
    let hiddenSymbolLayerCount = 0;
    let keptMajorLabelLayerCount = 0;

    layers.forEach((layer) => {
        if (layer.type !== 'symbol') return;

        if (shouldKeepMajorLabelLayer(layer)) {
            keptMajorLabelLayerCount += 1;
            return;
        }

        try {
            const visibility = mapRef.getLayoutProperty?.(layer.id, 'visibility');
            if (visibility !== 'none') {
                mapRef.setLayoutProperty(layer.id, 'visibility', 'none');
                hiddenSymbolLayerCount += 1;
            }
        } catch (error) {
            console.warn(`${logPrefix}: Failed to hide symbol layer "${layer.id}"`, error);
        }
    });

    console.log(
        `${logPrefix}: Reduced label density (kept ${keptMajorLabelLayerCount} major label layers, hid ${hiddenSymbolLayerCount} symbol layers)`
    );
};
const selectDiverseCameraCandidates = (candidates, limit = DEFAULT_ROBUST_CAMERA_RETRY_LIMIT) => {
    const sortedCandidates = [...candidates].sort((leftCandidate, rightCandidate) => rightCandidate.score - leftCandidate.score);
    const selectedCandidates = [];
    const selectedCandidateKeys = new Set();
    const separationPasses = [60, 35, 20, 0];

    separationPasses.forEach((minimumBearingSeparation) => {
        if (selectedCandidates.length >= limit) return;

        sortedCandidates.forEach((candidate) => {
            if (selectedCandidates.length >= limit) return;

            const candidateKey = `${normalizeBearing(candidate.bearing).toFixed(2)}|${candidate.pitch.toFixed(2)}`;
            if (selectedCandidateKeys.has(candidateKey)) return;

            const isSeparatedEnough = selectedCandidates.every((selectedCandidate) => (
                Math.abs(getBearingDelta(selectedCandidate.bearing, candidate.bearing)) >= minimumBearingSeparation
            ));
            if (!isSeparatedEnough) return;

            selectedCandidates.push(candidate);
            selectedCandidateKeys.add(candidateKey);
        });
    });

    return selectedCandidates;
};
const clampPitch = (nextPitch) => Math.max(MIN_CAMERA_SEARCH_PITCH, Math.min(MAX_CAMERA_SEARCH_PITCH, nextPitch));
const hashString = (value = '') => {
    let hash = 5381;
    for (let i = 0; i < value.length; i += 1) {
        hash = ((hash << 5) + hash) + value.charCodeAt(i);
    }
    return Math.abs(hash >>> 0).toString(36);
};

const getMapDisplayDimensions = (mapRef) => {
    const container = mapRef?.getContainer?.();
    const canvas = mapRef?.getCanvas?.();
    const width = container?.clientWidth || canvas?.clientWidth || canvas?.width || 0;
    const height = container?.clientHeight || canvas?.clientHeight || canvas?.height || 0;
    return {
        width: Math.max(1, Math.round(width)),
        height: Math.max(1, Math.round(height))
    };
};

const isVisibilityProbePixel = (r, g, b, a) => {
    if (a < 96) return false;

    const colorDistance =
        Math.abs(r - VISIBILITY_PROBE_COLOR.r) +
        Math.abs(g - VISIBILITY_PROBE_COLOR.g) +
        Math.abs(b - VISIBILITY_PROBE_COLOR.b);
    const magentaStrength = ((r + b) / 2) - g;

    return colorDistance <= VISIBILITY_PROBE_COLOR_DISTANCE_THRESHOLD &&
        magentaStrength >= VISIBILITY_PROBE_MIN_STRENGTH;
};

const EMPTY_ROUTE_CONTINUITY_METRICS = {
    visibleSampleCount: 0,
    totalSampleCount: 0,
    visibleSampleRatio: 0,
    internalGapCount: 0,
    internalGapRatio: 1,
    breakMarkers: []
};

const isVisibilityProbeNearPixel = (data, width, height, centerX, centerY, radiusX, radiusY) => {
    const safeRadiusX = Math.max(1, radiusX);
    const safeRadiusY = Math.max(1, radiusY);
    const minX = Math.max(0, centerX - safeRadiusX);
    const maxX = Math.min(width - 1, centerX + safeRadiusX);
    const minY = Math.max(0, centerY - safeRadiusY);
    const maxY = Math.min(height - 1, centerY + safeRadiusY);

    for (let y = minY; y <= maxY; y += 1) {
        for (let x = minX; x <= maxX; x += 1) {
            const dx = x - centerX;
            const dy = y - centerY;
            const normalizedDistance =
                ((dx * dx) / (safeRadiusX * safeRadiusX)) +
                ((dy * dy) / (safeRadiusY * safeRadiusY));
            if (normalizedDistance > 1) continue;

            const pixelIndex = ((y * width) + x) * 4;
            if (isVisibilityProbePixel(
                data[pixelIndex],
                data[pixelIndex + 1],
                data[pixelIndex + 2],
                data[pixelIndex + 3]
            )) {
                return true;
            }
        }
    }

    return false;
};

const ActivityMap3D = ({
    activity,
    selectedSegment = null, // When provided, use segment polyline instead of activity polyline
    width = DEFAULT_MAP_CAPTURE_WIDTH,
    height = DEFAULT_MAP_CAPTURE_HEIGHT,
    displayWidth = null, // Visual display size (if different from render size)
    displayHeight = null, // Visual display size (if different from render size)
    onCapture = null,
    onMapInteraction = null,
    onRenderStart = null,
    onRenderReady = null,
    onRenderError = null,
    onRenderComplete = null,
    onProgressChange = null,
    onProgressMetrics = null,
    onDebugCandidate = null,
    onDebugSummary = null,
    enableDebugCandidates = false,
    debugTransitionDelayMs = 0,
    mapStyleUrl = 'mapbox://styles/samuelcollin3333/cmf525thz00xg01rk0ewt0i6f',
    autoBearing = true, // Auto-calculate bearing so route goes bottom-left to top-right
    bearing = 0, // Only used if autoBearing is false
    pitch = 60,
    zoom = null,
    lineWidth = 20,
    enableCameraSearch = true,
    coarseBearingCount = DEFAULT_COARSE_BEARING_COUNT,
    robustRetryLimit = DEFAULT_ROBUST_CAMERA_RETRY_LIMIT,
    coverageRewardMultiplier = DEFAULT_COVERAGE_REWARD_MULTIPLIER,
    footprintRewardMultiplier = DEFAULT_FOOTPRINT_REWARD_MULTIPLIER,
    highDPI = true, // Enable high DPI rendering by default
    showLoadingOverlay = true
}) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [hasBeenInteracted, setHasBeenInteracted] = useState(false);
    const [progressText, setProgressText] = useState('Loading 3D map...');
    const captureCallbackRef = useRef(async () => null);
    const mapInteractionCallbackRef = useRef(onMapInteraction);
    const finalRenderResolvedRef = useRef(false);
    const hasBeenInteractedRef = useRef(false);
    const onRenderStartRef = useRef(onRenderStart);
    const onRenderReadyRef = useRef(onRenderReady);
    const onRenderErrorRef = useRef(onRenderError);
    const onRenderCompleteRef = useRef(onRenderComplete);
    const onProgressChangeRef = useRef(onProgressChange);
    const onProgressMetricsRef = useRef(onProgressMetrics);
    const onDebugCandidateRef = useRef(onDebugCandidate);
    const onDebugSummaryRef = useRef(onDebugSummary);
    const enableDebugCandidatesRef = useRef(enableDebugCandidates);
    const debugTransitionDelayMsRef = useRef(debugTransitionDelayMs);
    const suppressInteractionRef = useRef(false);

    useEffect(() => {
        mapInteractionCallbackRef.current = onMapInteraction;
    }, [onMapInteraction]);

    useEffect(() => {
        onRenderStartRef.current = onRenderStart;
    }, [onRenderStart]);

    useEffect(() => {
        onRenderReadyRef.current = onRenderReady;
    }, [onRenderReady]);

    useEffect(() => {
        onRenderErrorRef.current = onRenderError;
    }, [onRenderError]);

    useEffect(() => {
        onRenderCompleteRef.current = onRenderComplete;
    }, [onRenderComplete]);

    useEffect(() => {
        onProgressChangeRef.current = onProgressChange;
    }, [onProgressChange]);

    useEffect(() => {
        onProgressMetricsRef.current = onProgressMetrics;
    }, [onProgressMetrics]);

    useEffect(() => {
        onDebugCandidateRef.current = onDebugCandidate;
    }, [onDebugCandidate]);

    useEffect(() => {
        onDebugSummaryRef.current = onDebugSummary;
    }, [onDebugSummary]);

    useEffect(() => {
        enableDebugCandidatesRef.current = enableDebugCandidates;
    }, [enableDebugCandidates]);

    useEffect(() => {
        debugTransitionDelayMsRef.current = debugTransitionDelayMs;
    }, [debugTransitionDelayMs]);

    const setRenderProgress = useCallback((nextProgress, progressRatio = null) => {
        setProgressText(nextProgress);
        onProgressChangeRef.current?.(nextProgress);
        onProgressMetricsRef.current?.({
            label: nextProgress,
            progressRatio
        });
    }, []);

    const copyCanvasTo2D = useCallback((sourceCanvas, targetWidth = sourceCanvas?.width, targetHeight = sourceCanvas?.height) => {
        if (!sourceCanvas || !targetWidth || !targetHeight) return null;

        const snapshotCanvas = document.createElement('canvas');
        snapshotCanvas.width = Math.max(1, Math.round(targetWidth));
        snapshotCanvas.height = Math.max(1, Math.round(targetHeight));

        const context = snapshotCanvas.getContext('2d', { willReadFrequently: true });
        if (!context) return null;

        context.drawImage(sourceCanvas, 0, 0, snapshotCanvas.width, snapshotCanvas.height);
        return snapshotCanvas;
    }, []);

    const buildDebugThumbnail = useCallback((canvas, options = {}) => {
        if (!canvas) return null;
        const {
            maxWidth = 220,
            metrics = null,
            adaptivePadding = 0
        } = options;
        const scale = Math.min(1, maxWidth / canvas.width);
        const thumbnailCanvas = copyCanvasTo2D(
            canvas,
            Math.max(1, Math.round(canvas.width * scale)),
            Math.max(1, Math.round(canvas.height * scale))
        );
        if (!thumbnailCanvas) return null;
        const context = thumbnailCanvas.getContext('2d');
        if (context && metrics) {
            const scaleX = thumbnailCanvas.width / Math.max(1, metrics.displayWidth || canvas.clientWidth || canvas.width);
            const scaleY = thumbnailCanvas.height / Math.max(1, metrics.displayHeight || canvas.clientHeight || canvas.height);
            const safeFrameX = adaptivePadding * scaleX;
            const safeFrameY = adaptivePadding * scaleY;
            const safeFrameWidth = thumbnailCanvas.width - (safeFrameX * 2);
            const safeFrameHeight = thumbnailCanvas.height - (safeFrameY * 2);
            context.save();
            context.lineJoin = 'round';

            context.strokeStyle = 'rgba(255, 255, 255, 0.85)';
            context.lineWidth = 2;
            context.setLineDash([6, 4]);
            context.strokeRect(safeFrameX, safeFrameY, safeFrameWidth, safeFrameHeight);

            context.setLineDash([]);

            if (Array.isArray(metrics.breakMarkers) && metrics.breakMarkers.length > 0) {
                metrics.breakMarkers.forEach((marker) => {
                    const entryX = marker.entryX * scaleX;
                    const entryY = marker.entryY * scaleY;
                    const exitX = marker.exitX * scaleX;
                    const exitY = marker.exitY * scaleY;
                    const midX = marker.midX * scaleX;
                    const midY = marker.midY * scaleY;

                    context.save();
                    context.lineCap = 'round';
                    context.strokeStyle = 'rgba(255, 255, 255, 0.95)';
                    context.lineWidth = 5;
                    context.setLineDash([8, 6]);
                    context.beginPath();
                    context.moveTo(entryX, entryY);
                    context.lineTo(exitX, exitY);
                    context.stroke();

                    context.strokeStyle = 'rgba(255, 59, 48, 0.98)';
                    context.lineWidth = 3;
                    context.beginPath();
                    context.moveTo(entryX, entryY);
                    context.lineTo(exitX, exitY);
                    context.stroke();
                    context.setLineDash([]);

                    context.fillStyle = 'rgba(255, 59, 48, 0.98)';
                    context.strokeStyle = 'rgba(255, 255, 255, 0.95)';
                    context.lineWidth = 2;

                    context.beginPath();
                    context.arc(entryX, entryY, 4, 0, Math.PI * 2);
                    context.fill();
                    context.stroke();

                    context.beginPath();
                    context.arc(exitX, exitY, 4, 0, Math.PI * 2);
                    context.fill();
                    context.stroke();

                    context.beginPath();
                    context.arc(midX, midY, 5, 0, Math.PI * 2);
                    context.fill();
                    context.stroke();
                    context.restore();
                });
            }

            context.restore();
        }
        return thumbnailCanvas.toDataURL('image/jpeg', 0.72);
    }, [copyCanvasTo2D]);

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    if (!blob) {
        reject(new Error('No blob provided for conversion'));
        return;
    }

    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const captureCanvasBlob = (canvas, type = 'image/png', quality) => new Promise((resolve) => {
    canvas.toBlob((blob) => {
        resolve(blob);
    }, type, quality);
});

const waitForAnimationFrames = (frameCount = 2) => new Promise((resolve) => {
    let framesRemaining = frameCount;

    const tick = () => {
        framesRemaining -= 1;
        if (framesRemaining <= 0) {
            resolve();
            return;
        }
        requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
});

const waitForMilliseconds = (milliseconds) => new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
});

const ensureNextRender = useCallback((label = 'capture', timeout = CAPTURE_RENDER_TIMEOUT, requireFreshRender = false) => {
        if (!map.current) return Promise.resolve();

        return new Promise((resolve) => {
            let settled = false;
            let timeoutId;
            let sawFreshRender = !requireFreshRender;

            const renderHandler = () => {
                sawFreshRender = true;
                checkTiles();
            };
            const idleHandler = () => checkTiles();
            const styleHandler = () => checkTiles();
            const sourceHandler = (event) => {
                if (event?.isSourceLoaded) {
                    checkTiles();
                }
            };
            const dataHandler = () => checkTiles();
            const dataloading = () => {
                // Track that data is loading
            };

            const detachListeners = () => {
                const mapRef = map.current;
                if (!mapRef) return;
                mapRef.off('render', renderHandler);
                mapRef.off('idle', idleHandler);
                mapRef.off('styledata', styleHandler);
                mapRef.off('sourcedata', sourceHandler);
                mapRef.off('data', dataHandler);
                mapRef.off('dataloading', dataloading);
            };

            const finish = (reason) => {
                if (settled) return;
                settled = true;
                if (timeoutId) clearTimeout(timeoutId);
                detachListeners();
                console.log(`ActivityMap3D: Render settle complete (${label}${reason ? ` - ${reason}` : ''})`);
                resolve();
            };

            const checkTiles = () => {
                const mapRef = map.current;
                if (!mapRef) return;
                if (requireFreshRender && !sawFreshRender) return;

                const tilesLoaded = mapRef.areTilesLoaded?.() ?? false;
                const styleLoaded = mapRef.isStyleLoaded?.() ?? false;
                const fullyLoaded = mapRef.loaded?.() ?? false;

                // Additional check for source loading
                let allSourcesLoaded = true;
                const style = mapRef.getStyle?.();
                if (style && style.sources) {
                    for (const sourceId in style.sources) {
                        if (mapRef.isSourceLoaded && !mapRef.isSourceLoaded(sourceId)) {
                            allSourcesLoaded = false;
                            break;
                        }
                    }
                }

                if (tilesLoaded && styleLoaded && fullyLoaded && allSourcesLoaded) {
                    finish('tiles-loaded');
                }
            };

            const mapRef = map.current;
            if (mapRef) {
                mapRef.on('render', renderHandler);
                mapRef.on('idle', idleHandler);
                mapRef.on('styledata', styleHandler);
                mapRef.on('sourcedata', sourceHandler);
                mapRef.on('data', dataHandler);
                mapRef.on('dataloading', dataloading);
            }

            if (!requireFreshRender) {
                map.current.triggerRepaint();
                checkTiles();
            }

            timeoutId = setTimeout(() => {
                console.warn(`ActivityMap3D: Render settle timeout (${label}), proceeding`);
                finish('timeout');
            }, timeout);
        });
    }, []);

    const scheduleFinalRenderCheck = useCallback(() => {
        if (!map.current || finalRenderResolvedRef.current) return;

        const confirmFinal = () => {
            if (!map.current || finalRenderResolvedRef.current) return;

            const styleLoaded = map.current.isStyleLoaded?.();
            const tilesLoaded = map.current.areTilesLoaded?.();
            const fullyLoaded = map.current.loaded?.();

            if (styleLoaded && tilesLoaded && fullyLoaded) {
                finalRenderResolvedRef.current = true;
                onRenderReadyRef.current?.();
                onRenderCompleteRef.current?.();
                return;
            }

            map.current.once('idle', confirmFinal);
        };

        map.current.once('idle', confirmFinal);
    }, []);

    const getPolylineString = useCallback(() => {
        const segmentPolylineData = selectedSegment?.segment?.map?.polyline || selectedSegment?.segment?.map?.summary_polyline;
        return segmentPolylineData || activity?.map?.summary_polyline || null;
    }, [activity, selectedSegment]);

    // Decode polyline to coordinates
    const decodePolyline = useCallback(() => {
        const polylineToUse = getPolylineString();
        const isSegmentPolyline = !!(selectedSegment?.segment?.map?.polyline || selectedSegment?.segment?.map?.summary_polyline);

        if (!polylineToUse) {
            console.log('ActivityMap3D: No polyline found (checked segment and activity)');
            return null;
        }

        try {
            console.log(`ActivityMap3D: Starting polyline decode (${isSegmentPolyline ? 'segment' : 'activity'}), polyline length:`, polylineToUse.length);
            const decoded = polyline.decode(polylineToUse);
            console.log('ActivityMap3D: Raw decoded coordinates count:', decoded.length);
            console.log('ActivityMap3D: First 3 raw decoded coords:', decoded.slice(0, 3));

            // Validate coordinates and filter out any invalid ones
            const validCoords = decoded
                .map((coord, idx) => {
                    const swapped = [coord[1], coord[0]]; // Swap lat/lng to lng/lat
                    if (idx < 3) {
                        console.log(`ActivityMap3D: Coord ${idx} - raw: [${coord[0]}, ${coord[1]}], swapped: [${swapped[0]}, ${swapped[1]}]`);
                    }
                    return swapped;
                })
                .filter((coord, idx) => {
                    const isValid = !isNaN(coord[0]) && !isNaN(coord[1]) &&
                                  Math.abs(coord[0]) <= 180 && Math.abs(coord[1]) <= 90;
                    if (!isValid) {
                        console.warn(`ActivityMap3D: Invalid coordinate at index ${idx}:`, coord,
                                   'isNaN checks:', !isNaN(coord[0]), !isNaN(coord[1]));
                    }
                    return isValid;
                });

            if (validCoords.length === 0) {
                console.error('ActivityMap3D: No valid coordinates after filtering');
                return null;
            }

            console.log(`ActivityMap3D: Decoded ${validCoords.length} valid coordinates from polyline`);
            console.log('ActivityMap3D: First valid coord:', validCoords[0]);
            console.log('ActivityMap3D: Last valid coord:', validCoords[validCoords.length - 1]);
            console.log('ActivityMap3D: Middle valid coord:', validCoords[Math.floor(validCoords.length / 2)]);
            return validCoords;
        } catch (error) {
            console.error('Error decoding polyline:', error);
            return null;
        }
    }, [getPolylineString, selectedSegment]);

    const getRouteBounds = useCallback((coords) => {
        return coords.reduce(
            (bounds, coord) => bounds.extend(coord),
            new mapboxgl.LngLatBounds(coords[0], coords[0])
        );
    }, []);

    const getAdaptivePadding = useCallback((mapRef) => {
        const canvas = mapRef?.getCanvas?.();
        const mapWidth = canvas?.width || 0;
        const mapHeight = canvas?.height || 0;
        const minDimension = Math.min(mapWidth, mapHeight);

        if (minDimension < 150) return 10;
        if (minDimension < 250) return 15;
        if (minDimension < 400) return 25;
        return Math.min(50, Math.floor(minDimension * 0.08));
    }, []);

    // Initialize map
    // Capture map as image
    const captureMap = useCallback(async () => {
        if (!map.current) return null;

        let shouldRestoreProbe = false;
        try {
            console.log('ActivityMap3D: Starting map capture');

            // Get current camera state for debugging
            const center = map.current.getCenter();
            const zoom = map.current.getZoom();
            const bearing = map.current.getBearing();
            const pitch = map.current.getPitch();
            console.log(`ActivityMap3D: Current camera - center: [${center.lng.toFixed(4)}, ${center.lat.toFixed(4)}], zoom: ${zoom.toFixed(1)}, bearing: ${bearing.toFixed(0)}, pitch: ${pitch.toFixed(0)}`);

            // If map is currently moving, wait for it to stop
            if (map.current.isMoving()) {
                console.log('ActivityMap3D: Map is moving, waiting for idle...');
                await new Promise(resolve => {
                    map.current.once('idle', resolve);
                });
            }

            // Ensure map is fully loaded
            if (!map.current.loaded()) {
                console.log('ActivityMap3D: Map not fully loaded, waiting...');
                // Set a timeout to prevent infinite waiting
                await Promise.race([
                    new Promise(resolve => {
                        map.current.once('load', () => {
                            console.log('ActivityMap3D: Map loaded event fired');
                            resolve();
                        });
                    }),
                    new Promise(resolve => {
                        // Check if map becomes loaded in intervals
                        const checkInterval = setInterval(() => {
                            if (map.current && map.current.loaded()) {
                                console.log('ActivityMap3D: Map is now loaded');
                                clearInterval(checkInterval);
                                resolve();
                            }
                        }, 100);
                        // Timeout after 5 seconds
                        setTimeout(() => {
                            clearInterval(checkInterval);
                            console.log('ActivityMap3D: Timeout waiting for map to load');
                            resolve();
                        }, 5000);
                    })
                ]);
            }

            if (map.current.getLayer(VISIBILITY_PROBE_LAYER_ID)) {
                const probeVisibility = map.current.getLayoutProperty(VISIBILITY_PROBE_LAYER_ID, 'visibility');
                shouldRestoreProbe = probeVisibility !== 'none';
                if (shouldRestoreProbe) {
                    console.log('ActivityMap3D: Hiding magenta probe before backend capture');
                    map.current.setLayoutProperty(VISIBILITY_PROBE_LAYER_ID, 'visibility', 'none');
                    await ensureNextRender('pre-capture-route-color-reset');
                }
            }

            // Map is already rendering at high resolution, just capture it
            const canvas = map.current.getCanvas();
            console.log(`ActivityMap3D: Capturing map at resolution: ${canvas.width}x${canvas.height}`);

            await ensureNextRender('pre-capture');

            // Wait for multiple frames to ensure all rendering is complete
            await new Promise((resolve) => {
                let frameCount = 0;
                const waitFrames = 3; // Wait for 3 animation frames
                const frameWait = () => {
                    frameCount++;
                    if (frameCount >= waitFrames) {
                        resolve();
                    } else {
                        requestAnimationFrame(frameWait);
                    }
                };
                requestAnimationFrame(frameWait);
            });

            let captureResult = null;
            try {
                console.log('ActivityMap3D: Getting canvas for capture');
                const finalCenter = map.current.getCenter();
                const finalZoom = map.current.getZoom();
                const finalBearing = map.current.getBearing();
                const finalPitch = map.current.getPitch();
                console.log(`ActivityMap3D: Final camera at capture - center: [${finalCenter.lng.toFixed(4)}, ${finalCenter.lat.toFixed(4)}], zoom: ${finalZoom.toFixed(1)}, bearing: ${finalBearing.toFixed(0)}, pitch: ${finalPitch.toFixed(0)}`);
                console.log(`ActivityMap3D: Map was interacted with: ${hasBeenInteracted}`);

                const captureMimeType = 'image/jpeg';
                const captureQuality = 0.9; // Balance fidelity with upload size

                let blob = await captureCanvasBlob(canvas, captureMimeType, captureQuality);

                if (!blob) {
                    throw new Error('ActivityMap3D: Failed to create capture blob');
                }

                let mimeType = blob.type || captureMimeType;
                let dataUrl = await blobToDataUrl(blob);

                captureResult = {
                    dataUrl,
                    blob,
                    mimeType,
                };

                if (captureResult) {
                    console.log('ActivityMap3D: Stored captured image, blob size:', blob.size, 'bytes');
                }
            } catch (error) {
                console.error('Error during canvas capture:', error);
            }

            return captureResult;
        } catch (error) {
            console.error('Error capturing map:', error);
            return null;
        } finally {
            if (shouldRestoreProbe && map.current?.getLayer(VISIBILITY_PROBE_LAYER_ID)) {
                try {
                    console.log('ActivityMap3D: Restoring magenta probe after backend capture');
                    map.current.setLayoutProperty(VISIBILITY_PROBE_LAYER_ID, 'visibility', 'visible');
                    await ensureNextRender('post-capture-restore-probe');
                } catch (restoreError) {
                    console.warn('ActivityMap3D: Failed to restore magenta probe after capture', restoreError);
                }
            }
        }
    }, [hasBeenInteracted, ensureNextRender]);

    useEffect(() => {
        captureCallbackRef.current = async () => {
            console.log('ActivityMap3D: Capturing current map view for backend');
            return await captureMap();
        };

        if (onCapture) {
            onCapture(() => captureCallbackRef.current());
        }
    }, [captureMap, onCapture]);

    const calculateStartToEndBearing = useCallback((coords) => {
        if (!coords || coords.length < 2) return 0;

        const start = coords[0]; // [lng, lat]
        const end = coords[coords.length - 1]; // [lng, lat]

        // Calculate bearing from start to end using the Haversine formula
        const startLat = start[1] * Math.PI / 180;
        const endLat = end[1] * Math.PI / 180;
        const deltaLng = (end[0] - start[0]) * Math.PI / 180;

        const y = Math.sin(deltaLng) * Math.cos(endLat);
        const x = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(deltaLng);
        let bearingRad = Math.atan2(y, x);
        let bearingDeg = bearingRad * 180 / Math.PI;

        // Normalize to 0-360
        bearingDeg = (bearingDeg + 360) % 360;

        const adjustedBearing = bearingDeg - SCREEN_DIAGONAL_BEARING_OFFSET;

        console.log(`ActivityMap3D: Route bearing from start to end: ${bearingDeg.toFixed(1)}°, adjusted bearing: ${adjustedBearing.toFixed(1)}°`);

        return adjustedBearing;
    }, []);

    const calculatePrincipalAxisBearing = useCallback((coords) => {
        if (!coords || coords.length < 2) return 0;

        const latitudes = coords.map((coord) => coord[1]);
        const meanLat = latitudes.reduce((sum, value) => sum + value, 0) / latitudes.length;
        const lngScale = Math.cos(meanLat * Math.PI / 180) || 1;

        const normalizedCoords = coords.map((coord) => ({
            x: coord[0] * lngScale,
            y: coord[1]
        }));

        const meanX = normalizedCoords.reduce((sum, coord) => sum + coord.x, 0) / normalizedCoords.length;
        const meanY = normalizedCoords.reduce((sum, coord) => sum + coord.y, 0) / normalizedCoords.length;

        let sxx = 0;
        let syy = 0;
        let sxy = 0;
        normalizedCoords.forEach((coord) => {
            const dx = coord.x - meanX;
            const dy = coord.y - meanY;
            sxx += dx * dx;
            syy += dy * dy;
            sxy += dx * dy;
        });

        if (sxx === 0 && syy === 0) {
            return calculateStartToEndBearing(coords);
        }

        const axisRadians = 0.5 * Math.atan2(2 * sxy, sxx - syy);
        const axisDegreesFromEast = axisRadians * 180 / Math.PI;
        const routeBearing = normalizeBearing(90 - axisDegreesFromEast);
        const adjustedBearing = routeBearing - SCREEN_DIAGONAL_BEARING_OFFSET;

        console.log(`ActivityMap3D: Principal axis bearing: ${routeBearing.toFixed(1)}°, adjusted bearing: ${adjustedBearing.toFixed(1)}°`);

        return adjustedBearing;
    }, [calculateStartToEndBearing]);

    const effectiveCoarseBearingCount = resolveCoarseBearingCount(coarseBearingCount);
    const effectiveCoarseBearingStepDegrees = getCoarseBearingStepDegrees(effectiveCoarseBearingCount);
    const effectiveRobustRetryLimit = resolveRobustRetryLimit(robustRetryLimit);

    const getCameraSearchCacheKey = useCallback((polylineString) => {
        const effectiveCoverageRewardMultiplier = resolveCoverageRewardMultiplier(coverageRewardMultiplier);
        const effectiveFootprintRewardMultiplier = resolveFootprintRewardMultiplier(footprintRewardMultiplier);
        return [
            mapStyleUrl,
            width,
            height,
            lineWidth,
            effectiveCoarseBearingCount,
            getUniqueSearchPitches(PRIMARY_CAMERA_SEARCH_PITCH).join(','),
            effectiveCoverageRewardMultiplier.toFixed(3),
            effectiveFootprintRewardMultiplier.toFixed(3),
            hashString(polylineString || '')
        ].join('|');
    }, [mapStyleUrl, width, height, lineWidth, effectiveCoarseBearingCount, coverageRewardMultiplier, footprintRewardMultiplier]);

    const setProbeVisibility = useCallback((isVisible) => {
        if (!map.current?.getLayer(VISIBILITY_PROBE_LAYER_ID)) return;
        map.current.setLayoutProperty(VISIBILITY_PROBE_LAYER_ID, 'visibility', isVisible ? 'visible' : 'none');
    }, []);

    const buildCoarseCandidates = useCallback((seedBearings, targetPitch) => {
        const normalizedBearings = new Set();
        const candidates = [];
        const seeds = Array.isArray(seedBearings) ? seedBearings : [seedBearings];
        const bearingSeed = normalizeBearing(seeds.find(Number.isFinite) ?? 0);
        const coarsePitches = getUniqueSearchPitches(targetPitch);

        for (let stepIndex = 0; stepIndex < effectiveCoarseBearingCount; stepIndex += 1) {
            const candidateBearing = normalizeBearing(bearingSeed + (stepIndex * effectiveCoarseBearingStepDegrees));
            const bearingKey = candidateBearing.toFixed(2);
            if (normalizedBearings.has(bearingKey)) continue;
            normalizedBearings.add(bearingKey);
            coarsePitches.forEach((candidatePitch) => {
                candidates.push({
                    bearing: candidateBearing,
                    pitch: candidatePitch
                });
            });
        }

        return candidates;
    }, [effectiveCoarseBearingCount, effectiveCoarseBearingStepDegrees]);

    const getCurrentCameraState = useCallback(() => {
        if (!map.current) return null;

        const center = map.current.getCenter();
        return {
            center: [center.lng, center.lat],
            zoom: map.current.getZoom(),
            bearing: map.current.getBearing(),
            pitch: map.current.getPitch()
        };
    }, []);

    const waitForCameraState = useCallback((targetCamera, timeout = 600) => {
        if (!map.current || !targetCamera) return Promise.resolve();

        return new Promise((resolve) => {
            const startTime = Date.now();

            const checkCameraState = () => {
                if (!map.current) {
                    resolve();
                    return;
                }

                const center = map.current.getCenter();
                const currentState = {
                    center: [center.lng, center.lat],
                    zoom: map.current.getZoom(),
                    bearing: map.current.getBearing(),
                    pitch: map.current.getPitch()
                };

                const bearingMatches = Math.abs(getBearingDelta(currentState.bearing, targetCamera.bearing ?? currentState.bearing)) < 0.5;
                const pitchMatches = Math.abs(currentState.pitch - (targetCamera.pitch ?? currentState.pitch)) < 0.5;
                const zoomMatches = Math.abs(currentState.zoom - (targetCamera.zoom ?? currentState.zoom)) < 0.05;

                if (bearingMatches && pitchMatches && zoomMatches) {
                    resolve();
                    return;
                }

                if (Date.now() - startTime >= timeout) {
                    resolve();
                    return;
                }

                requestAnimationFrame(checkCameraState);
            };

            requestAnimationFrame(checkCameraState);
        });
    }, []);

    const applyCameraCandidate = useCallback(async (bounds, adaptivePadding, cameraCandidate, label, options = {}) => {
        if (!map.current) return null;

        suppressInteractionRef.current = true;
        try {
            const lockedZoom = Number.isFinite(options?.lockedZoom) ? options.lockedZoom : null;
            let targetCamera = null;
            let cameraForBoundsRejected = false;

            if (typeof map.current.cameraForBounds === 'function') {
                targetCamera = map.current.cameraForBounds(bounds, {
                    padding: adaptivePadding,
                    bearing: cameraCandidate.bearing,
                    pitch: cameraCandidate.pitch,
                    maxZoom: zoom || undefined
                });
            }

            if (typeof map.current.cameraForBounds === 'function' && (!targetCamera?.center || !Number.isFinite(targetCamera?.zoom))) {
                cameraForBoundsRejected = true;
                console.warn('ActivityMap3D: cameraForBounds rejected candidate, forcing preview anyway:', cameraCandidate);
            }

            const nextRenderPromise = ensureNextRender(label, 3000, true);

            map.current.stop?.();

            if (targetCamera?.center && Number.isFinite(targetCamera?.zoom)) {
                if (lockedZoom !== null) {
                    targetCamera = {
                        ...targetCamera,
                        zoom: lockedZoom
                    };
                }
                if (enableDebugCandidatesRef.current) {
                    const moveEndPromise = new Promise((resolve) => {
                        map.current?.once?.('moveend', resolve);
                    });

                    map.current.easeTo({
                        center: targetCamera.center,
                        zoom: lockedZoom ?? targetCamera.zoom,
                        bearing: targetCamera.bearing,
                        pitch: targetCamera.pitch,
                        duration: 1200,
                        easing: (t) => t,
                        essential: true
                    });

                    await moveEndPromise;
                } else {
                    map.current.jumpTo({
                        center: targetCamera.center,
                        zoom: lockedZoom ?? targetCamera.zoom,
                        bearing: targetCamera.bearing,
                        pitch: targetCamera.pitch
                    });
                }
            } else {
                map.current.fitBounds(bounds, {
                    padding: adaptivePadding,
                    bearing: cameraCandidate.bearing,
                    pitch: cameraCandidate.pitch,
                    linear: true,
                    duration: 0,
                    animate: false,
                    maxZoom: zoom || undefined
                });
                targetCamera = getCurrentCameraState();

                if ((cameraForBoundsRejected || lockedZoom !== null) && targetCamera?.center && Number.isFinite(targetCamera?.zoom)) {
                    map.current.jumpTo({
                        center: targetCamera.center,
                        zoom: lockedZoom ?? targetCamera.zoom,
                        bearing: cameraCandidate.bearing,
                        pitch: cameraCandidate.pitch
                    });
                    targetCamera = getCurrentCameraState();
                }
            }

            map.current.triggerRepaint();
            await waitForCameraState(targetCamera || {
                bearing: cameraCandidate.bearing,
                pitch: cameraCandidate.pitch
            });
            await nextRenderPromise;
            await waitForAnimationFrames(1);
            const resolvedCamera = targetCamera || getCurrentCameraState();
            return resolvedCamera ? {
                ...resolvedCamera,
                cameraForBoundsRejected
            } : null;
        } catch (error) {
            console.error('ActivityMap3D: Failed to apply camera candidate:', cameraCandidate, error);
            return null;
        } finally {
            await waitForAnimationFrames(1);
            suppressInteractionRef.current = false;
        }
    }, [ensureNextRender, getCurrentCameraState, waitForCameraState, zoom]);

    const applyInitialMainCamera = useCallback(async (bounds, adaptivePadding, cameraBearing, cameraPitch, label) => {
        if (!map.current) return null;

        suppressInteractionRef.current = true;
        try {
            const nextRenderPromise = ensureNextRender(label, 3000, true);
            const idlePromise = new Promise((resolve) => {
                map.current?.once?.('idle', resolve);
            });

            map.current.stop?.();
            map.current.fitBounds(bounds, {
                padding: adaptivePadding,
                bearing: cameraBearing,
                pitch: cameraPitch
            });
            map.current.triggerRepaint();

            await idlePromise;
            await nextRenderPromise;
            await waitForAnimationFrames(1);
            return getCurrentCameraState();
        } catch (error) {
            console.error('ActivityMap3D: Failed to apply initial main camera:', error);
            return null;
        } finally {
            await waitForAnimationFrames(1);
            suppressInteractionRef.current = false;
        }
    }, [ensureNextRender, getCurrentCameraState]);

    const emitDebugCandidate = useCallback((payload) => {
        if (!enableDebugCandidatesRef.current || !onDebugCandidateRef.current || !map.current) return;

        try {
            const thumbnailDataUrl = buildDebugThumbnail(map.current.getCanvas(), {
                metrics: payload,
                adaptivePadding: payload.adaptivePadding
            });
            const emittedPayload = {
                ...payload,
                id: `${payload.phase || 'candidate'}-${payload.index || 0}-${Date.now()}-${Math.round(Math.random() * 100000)}`,
                thumbnailDataUrl,
                camera: getCurrentCameraState()
            };
            onDebugCandidateRef.current({
                ...emittedPayload
            });
        } catch (error) {
            console.error('ActivityMap3D: Failed to emit debug candidate:', error);
        }
    }, [buildDebugThumbnail, getCurrentCameraState]);

    const emitDebugSummary = useCallback((summary) => {
        if (!onDebugSummaryRef.current) return;

        try {
            onDebugSummaryRef.current(summary);
        } catch (error) {
            console.error('ActivityMap3D: Failed to emit debug summary:', error);
        }
    }, []);

    const applyDirectCameraState = useCallback(async (cameraState, label) => {
        if (!map.current || !cameraState) return false;

        suppressInteractionRef.current = true;
        try {
            const centerArray = Array.isArray(cameraState.center) ? cameraState.center : null;
            const rawLng = centerArray?.[0];
            const rawLat = centerArray?.[1];
            const sanitizedCameraState = {
                ...cameraState,
                center: Number.isFinite(rawLng) && Number.isFinite(rawLat)
                    ? [rawLng, clampLatitude(rawLat)]
                    : null,
                zoom: Number.isFinite(cameraState.zoom) ? cameraState.zoom : null,
                bearing: Number.isFinite(cameraState.bearing) ? cameraState.bearing : 0,
                pitch: clampPitch(Number.isFinite(cameraState.pitch) ? cameraState.pitch : pitch)
            };
            if (!sanitizedCameraState.center || !Number.isFinite(sanitizedCameraState.zoom)) {
                console.warn('ActivityMap3D: Skipping invalid direct camera state:', cameraState);
                return false;
            }

            const nextRenderPromise = ensureNextRender(label, 3000, true);
            map.current.stop?.();
            map.current.jumpTo({
                center: sanitizedCameraState.center,
                zoom: sanitizedCameraState.zoom,
                bearing: sanitizedCameraState.bearing,
                pitch: sanitizedCameraState.pitch
            });
            map.current.triggerRepaint();
            await waitForCameraState(sanitizedCameraState);
            await nextRenderPromise;
            await waitForAnimationFrames(1);
            return true;
        } catch (error) {
            console.error('ActivityMap3D: Failed to apply direct camera state:', cameraState, error);
            return false;
        } finally {
            await waitForAnimationFrames(1);
            suppressInteractionRef.current = false;
        }
    }, [ensureNextRender, pitch, waitForCameraState]);

    const getProjectedRouteMetrics = useCallback((coords, adaptivePadding) => {
        const mapRef = map.current;
        if (!mapRef) {
            return null;
        }

        const canvas = mapRef.getCanvas();
        if (!coords?.length) {
            return null;
        }

        const { width: displayWidth, height: displayHeight } = getMapDisplayDimensions(mapRef);
        const renderWidth = canvas.width;
        const renderHeight = canvas.height;
        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        let onScreenCount = 0;
        let insidePaddingCount = 0;

        coords.forEach((coord) => {
            const point = mapRef.project(coord);
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
            minY = Math.min(minY, point.y);
            maxY = Math.max(maxY, point.y);

            const isOnScreen = point.x >= 0 && point.x <= displayWidth && point.y >= 0 && point.y <= displayHeight;
            if (isOnScreen) {
                onScreenCount += 1;
            }

            const isInsidePaddingFrame =
                point.x >= adaptivePadding &&
                point.x <= displayWidth - adaptivePadding &&
                point.y >= adaptivePadding &&
                point.y <= displayHeight - adaptivePadding;
            if (isInsidePaddingFrame) {
                insidePaddingCount += 1;
            }
        });

        if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
            return null;
        }

        const safeWidth = Math.max(1, displayWidth - (adaptivePadding * 2));
        const safeHeight = Math.max(1, displayHeight - (adaptivePadding * 2));
        const bboxWidth = Math.max(1, maxX - minX);
        const bboxHeight = Math.max(1, maxY - minY);
        const bboxCenterX = (minX + maxX) / 2;
        const bboxCenterY = (minY + maxY) / 2;
        const leftMargin = minX;
        const rightMargin = displayWidth - maxX;
        const topMargin = minY;
        const bottomMargin = displayHeight - maxY;
        const safeFrameMinX = adaptivePadding;
        const safeFrameMaxX = displayWidth - adaptivePadding;
        const safeFrameMinY = adaptivePadding;
        const safeFrameMaxY = displayHeight - adaptivePadding;
        const overlapMinX = Math.max(minX, safeFrameMinX);
        const overlapMaxX = Math.min(maxX, safeFrameMaxX);
        const overlapMinY = Math.max(minY, safeFrameMinY);
        const overlapMaxY = Math.min(maxY, safeFrameMaxY);
        const overlapWidth = Math.max(0, overlapMaxX - overlapMinX);
        const overlapHeight = Math.max(0, overlapMaxY - overlapMinY);
        const insidePaddingBboxArea = overlapWidth * overlapHeight;
        const outsidePaddingBboxArea = Math.max(0, (bboxWidth * bboxHeight) - insidePaddingBboxArea);
        const centerDistanceNormalized = Math.hypot(
            bboxCenterX - (displayWidth / 2),
            bboxCenterY - (displayHeight / 2)
        ) / Math.max(1, Math.hypot(displayWidth / 2, displayHeight / 2));

        return {
            canvasWidth: renderWidth,
            canvasHeight: renderHeight,
            renderWidth,
            renderHeight,
            displayWidth,
            displayHeight,
            minX,
            maxX,
            minY,
            maxY,
            safeWidth,
            safeHeight,
            bboxWidth,
            bboxHeight,
            bboxArea: bboxWidth * bboxHeight,
            bboxCenterX,
            bboxCenterY,
            leftMargin,
            rightMargin,
            topMargin,
            bottomMargin,
            insidePaddingBboxArea,
            outsidePaddingBboxArea,
            bboxWidthRatio: bboxWidth / safeWidth,
            bboxHeightRatio: bboxHeight / safeHeight,
            footprintRatio: (bboxWidth / safeWidth) * (bboxHeight / safeHeight),
            outsidePaddingAreaRatio: outsidePaddingBboxArea / Math.max(1, bboxWidth * bboxHeight),
            onScreenRatio: onScreenCount / coords.length,
            insidePaddingRatio: insidePaddingCount / coords.length,
            minMargin: Math.min(leftMargin, rightMargin, topMargin, bottomMargin),
            centerDistanceNormalized
        };
    }, []);

    const getRouteContinuityMetrics = useCallback((coords, metrics, imageData) => {
        const mapRef = map.current;
        if (!mapRef || !coords?.length || !metrics || !imageData) {
            return EMPTY_ROUTE_CONTINUITY_METRICS;
        }

        const projectedPoints = coords
            .map((coord) => mapRef.project(coord))
            .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

        if (!projectedPoints.length) {
            return EMPTY_ROUTE_CONTINUITY_METRICS;
        }

        const scaleX = imageData.width / Math.max(1, metrics.displayWidth);
        const scaleY = imageData.height / Math.max(1, metrics.displayHeight);
        const sampleSpacingDisplayPx = Math.max(2, Math.round(lineWidth * 0.5));
        const neighborhoodDisplayRadius = Math.max(3, Math.round(lineWidth * 0.7));
        const neighborhoodRadiusX = Math.max(1, Math.round(neighborhoodDisplayRadius * scaleX));
        const neighborhoodRadiusY = Math.max(1, Math.round(neighborhoodDisplayRadius * scaleY));
        const samplePoints = [];

        const pushVisibilitySample = (x, y) => {
            const renderX = Math.round(x * scaleX);
            const renderY = Math.round(y * scaleY);
            if (
                renderX < 0 ||
                renderX >= imageData.width ||
                renderY < 0 ||
                renderY >= imageData.height
            ) {
                samplePoints.push({
                    x,
                    y,
                    visible: false
                });
                return;
            }

            samplePoints.push({
                x,
                y,
                visible: isVisibilityProbeNearPixel(
                    imageData.data,
                    imageData.width,
                    imageData.height,
                    renderX,
                    renderY,
                    neighborhoodRadiusX,
                    neighborhoodRadiusY
                )
            });
        };

        if (projectedPoints.length === 1) {
            pushVisibilitySample(projectedPoints[0].x, projectedPoints[0].y);
        } else {
            for (let pointIndex = 1; pointIndex < projectedPoints.length; pointIndex += 1) {
                const startPoint = projectedPoints[pointIndex - 1];
                const endPoint = projectedPoints[pointIndex];
                const dx = endPoint.x - startPoint.x;
                const dy = endPoint.y - startPoint.y;
                const segmentLength = Math.hypot(dx, dy);
                const stepCount = Math.max(1, Math.ceil(segmentLength / sampleSpacingDisplayPx));

                for (let stepIndex = 0; stepIndex <= stepCount; stepIndex += 1) {
                    if (pointIndex > 1 && stepIndex === 0) continue;

                    const interpolation = stepIndex / stepCount;
                    pushVisibilitySample(
                        startPoint.x + (dx * interpolation),
                        startPoint.y + (dy * interpolation)
                    );
                }
            }
        }

        const totalSampleCount = samplePoints.length;
        if (!totalSampleCount) {
            return EMPTY_ROUTE_CONTINUITY_METRICS;
        }

        const visibleSampleCount = samplePoints.reduce((count, samplePoint) => count + (samplePoint.visible ? 1 : 0), 0);
        const firstVisibleIndex = samplePoints.findIndex((samplePoint) => samplePoint.visible);
        const lastVisibleIndex = (() => {
            for (let sampleIndex = samplePoints.length - 1; sampleIndex >= 0; sampleIndex -= 1) {
                if (samplePoints[sampleIndex].visible) {
                    return sampleIndex;
                }
            }
            return -1;
        })();

        if (firstVisibleIndex === -1 || lastVisibleIndex === -1) {
            return {
                ...EMPTY_ROUTE_CONTINUITY_METRICS,
                totalSampleCount
            };
        }

        const visibleSpanLength = Math.max(1, lastVisibleIndex - firstVisibleIndex + 1);
        let currentGapLength = 0;
        let internalGapCount = 0;
        let totalInternalHidden = 0;
        let currentGapStartIndex = null;
        const breakMarkers = [];

        for (let sampleIndex = firstVisibleIndex; sampleIndex <= lastVisibleIndex; sampleIndex += 1) {
            if (samplePoints[sampleIndex].visible) {
                if (currentGapLength > 0) {
                    internalGapCount += 1;
                    totalInternalHidden += currentGapLength;
                    if (currentGapStartIndex !== null && breakMarkers.length < MAX_DEBUG_BREAK_MARKERS) {
                        const gapEndIndex = sampleIndex - 1;
                        const entrySample = samplePoints[Math.max(firstVisibleIndex, currentGapStartIndex - 1)];
                        const exitSample = samplePoints[sampleIndex];
                        const midpointSample = samplePoints[Math.floor((currentGapStartIndex + gapEndIndex) / 2)];

                        if (entrySample && exitSample && midpointSample) {
                            breakMarkers.push({
                                entryX: entrySample.x,
                                entryY: entrySample.y,
                                exitX: exitSample.x,
                                exitY: exitSample.y,
                                midX: midpointSample.x,
                                midY: midpointSample.y,
                                hiddenSampleCount: currentGapLength
                            });
                        }
                    }
                    currentGapLength = 0;
                    currentGapStartIndex = null;
                }
            } else {
                if (currentGapStartIndex === null) {
                    currentGapStartIndex = sampleIndex;
                }
                currentGapLength += 1;
            }
        }

        return {
            visibleSampleCount,
            totalSampleCount,
            visibleSampleRatio: visibleSampleCount / totalSampleCount,
            internalGapCount,
            internalGapRatio: totalInternalHidden / visibleSpanLength,
            breakMarkers
        };
    }, [lineWidth]);

    const scoreCameraCandidate = useCallback((coords, cameraCandidate, adaptivePadding, options = {}) => {
        const {
            phase = 'default'
        } = options;
        const mapRef = map.current;
        if (!mapRef) {
            return {
                score: Number.NEGATIVE_INFINITY,
                visiblePixels: 0,
                ...EMPTY_ROUTE_CONTINUITY_METRICS,
                onScreenRatio: 0,
                insidePaddingRatio: 0,
                footprintRatio: 0,
                bboxWidthRatio: 0,
                bboxHeightRatio: 0,
                outsidePaddingAreaRatio: 1,
                centerDistanceNormalized: 1,
                minMargin: Number.NEGATIVE_INFINITY
            };
        }

        const metrics = getProjectedRouteMetrics(coords, adaptivePadding);
        if (!metrics) {
            return {
                score: Number.NEGATIVE_INFINITY,
                visiblePixels: 0,
                ...EMPTY_ROUTE_CONTINUITY_METRICS,
                onScreenRatio: 0,
                insidePaddingRatio: 0,
                footprintRatio: 0,
                bboxWidthRatio: 0,
                bboxHeightRatio: 0,
                outsidePaddingAreaRatio: 1,
                centerDistanceNormalized: 1,
                minMargin: Number.NEGATIVE_INFINITY
            };
        }

        const snapshotCanvas = copyCanvasTo2D(mapRef.getCanvas(), metrics.canvasWidth, metrics.canvasHeight);
        const context = snapshotCanvas?.getContext('2d', { willReadFrequently: true });
        if (!snapshotCanvas || !context) {
            return {
                score: Number.NEGATIVE_INFINITY,
                visiblePixels: 0,
                ...EMPTY_ROUTE_CONTINUITY_METRICS,
                ...metrics
            };
        }

        let visiblePixels = 0;
        let continuityMetrics = EMPTY_ROUTE_CONTINUITY_METRICS;
        try {
            const imageData = context.getImageData(0, 0, snapshotCanvas.width, snapshotCanvas.height);
            const data = imageData.data;
            for (let i = 0; i < data.length; i += 16) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const a = data[i + 3];
                if (isVisibilityProbePixel(r, g, b, a)) {
                    visiblePixels += 1;
                }
            }
            continuityMetrics = getRouteContinuityMetrics(coords, metrics, imageData);
        } catch (error) {
            console.error('ActivityMap3D: Unable to read canvas pixels for camera scoring:', error);
            return {
                score: Number.NEGATIVE_INFINITY,
                visiblePixels: 0,
                ...EMPTY_ROUTE_CONTINUITY_METRICS,
                ...metrics
            };
        }

        const pitchPenalty = Math.abs((cameraCandidate.pitch ?? pitch) - pitch) * 8;
        const effectiveCoverageRewardMultiplier = resolveCoverageRewardMultiplier(coverageRewardMultiplier);
        const effectiveFootprintRewardMultiplier = resolveFootprintRewardMultiplier(footprintRewardMultiplier);
        const footprintReward = Math.min(0.78, metrics.footprintRatio) * 4200 * effectiveFootprintRewardMultiplier;
        const coverageReward =
            (continuityMetrics.visibleSampleRatio * 3700) *
            effectiveCoverageRewardMultiplier;
        const centerPenaltyWeight = phase === 'coarse' ? 0 : 950;
        const centerPenalty = metrics.centerDistanceNormalized * centerPenaltyWeight;
        const clipPenalty = Math.max(0, adaptivePadding - metrics.minMargin) * 30;
        const outsidePaddingPenalty = metrics.outsidePaddingAreaRatio > 0 ? 100000 : 0;
        const score = (
            coverageReward +
            (metrics.onScreenRatio * 1400) +
            (metrics.insidePaddingRatio * 1100) +
            footprintReward -
            centerPenalty -
            clipPenalty -
            outsidePaddingPenalty -
            pitchPenalty
        );

        return {
            score,
            visiblePixels,
            coverageReward,
            footprintReward,
            coverageRewardMultiplier: effectiveCoverageRewardMultiplier,
            footprintRewardMultiplier: effectiveFootprintRewardMultiplier,
            ...continuityMetrics,
            ...metrics
        };
    }, [
        copyCanvasTo2D,
        coverageRewardMultiplier,
        footprintRewardMultiplier,
        getProjectedRouteMetrics,
        getRouteContinuityMetrics,
        pitch
    ]);

    const recenterCameraOnRoute = useCallback(async (coords, adaptivePadding, label, options = {}) => {
        const currentCamera = getCurrentCameraState();
        const metrics = getProjectedRouteMetrics(coords, adaptivePadding);
        if (!currentCamera || !metrics || !map.current) {
            return metrics;
        }

        const offsetX = metrics.bboxCenterX - (metrics.displayWidth / 2);
        const offsetY = metrics.bboxCenterY - (metrics.displayHeight / 2);
        const forceRecenter = Boolean(options?.force);
        if (!forceRecenter && Math.abs(offsetX) < 2 && Math.abs(offsetY) < 2) {
            return metrics;
        }

        if (
            !Number.isFinite(offsetX) ||
            !Number.isFinite(offsetY) ||
            !Number.isFinite(metrics.displayWidth) ||
            !Number.isFinite(metrics.displayHeight)
        ) {
            console.warn('ActivityMap3D: Skipping recenter because projected route center is invalid.', {
                bboxCenterX: metrics.bboxCenterX,
                bboxCenterY: metrics.bboxCenterY,
                offsetX,
                offsetY,
                displayWidth: metrics.displayWidth,
                displayHeight: metrics.displayHeight
            });
            return metrics;
        }

        try {
            suppressInteractionRef.current = true;
            const nextRenderPromise = ensureNextRender(label, 3000, true);
            const moveEndPromise = new Promise((resolve) => {
                map.current?.once?.('moveend', resolve);
            });

            map.current.stop?.();
            map.current.easeTo({
                center: currentCamera.center,
                zoom: currentCamera.zoom,
                bearing: currentCamera.bearing,
                pitch: currentCamera.pitch,
                offset: [-offsetX, -offsetY],
                duration: 0,
                easing: (t) => t,
                essential: true
            });
            map.current.triggerRepaint();

            await Promise.race([
                moveEndPromise,
                waitForMilliseconds(300)
            ]);
            await nextRenderPromise;
            await waitForAnimationFrames(1);
            return getProjectedRouteMetrics(coords, adaptivePadding);
        } catch (error) {
            console.warn('ActivityMap3D: Skipping recenter because screen-space pan failed.', {
                bboxCenterX: metrics.bboxCenterX,
                bboxCenterY: metrics.bboxCenterY,
                offsetX,
                offsetY,
                error: error?.message || error
            });
            return metrics;
        } finally {
            await waitForAnimationFrames(1);
            suppressInteractionRef.current = false;
        }
    }, [ensureNextRender, getCurrentCameraState, getProjectedRouteMetrics]);

    const refineCameraFraming = useCallback(async (coords, adaptivePadding, options = {}) => {
        if (!map.current) return null;
        const {
            selectionAttempt = null,
            selectionTotal = null
        } = options;

        setRenderProgress('Tightening 3D route...', 0.9);
        let bestMetrics = getProjectedRouteMetrics(coords, adaptivePadding);
        let bestState = getCurrentCameraState();
        if (!bestMetrics || !bestState) {
            return {
                cameraState: bestState,
                fragile: false,
                score: Number.NEGATIVE_INFINITY,
                scoreMetrics: EMPTY_ROUTE_CONTINUITY_METRICS,
                acceptedTightenSteps: 0,
                fragileReason: ''
            };
        }

        let bestScoreMetrics = scoreCameraCandidate(coords, bestState, adaptivePadding);
        let bestScore = bestScoreMetrics.score;
        const zoomStep = TIGHTEN_ZOOM_STEP;
        const maxZoomSteps = TIGHTEN_MAX_STEPS;
        let acceptedTightenSteps = 0;

        if ((bestScoreMetrics.outsidePaddingAreaRatio ?? 0) > 0) {
            console.warn('ActivityMap3D: Skipping tighten for selection candidate because the baseline framing is already out of bounds.', {
                selectionAttempt,
                selectionTotal,
                outsidePaddingAreaRatio: bestScoreMetrics.outsidePaddingAreaRatio
            });
            return {
                cameraState: bestState,
                fragile: false,
                score: bestScore,
                scoreMetrics: bestScoreMetrics,
                acceptedTightenSteps,
                fragileReason: ''
            };
        }

        for (let stepIndex = 0; stepIndex < maxZoomSteps; stepIndex += 1) {
            const tightenProgress = 0.92 + (((stepIndex + 1) / maxZoomSteps) * 0.06);
            setRenderProgress(`Tightening 3D framing ${stepIndex + 1}/${maxZoomSteps}...`, tightenProgress);
            const currentState = getCurrentCameraState();
            if (!currentState) break;

            const applied = await applyDirectCameraState({
                ...currentState,
                zoom: currentState.zoom + zoomStep
            }, `camera-tighten-${stepIndex + 1}`);
            if (!applied) break;

            const zoomedState = getCurrentCameraState();
            const zoomedScoreMetrics = zoomedState
                ? scoreCameraCandidate(coords, zoomedState, adaptivePadding)
                : null;
            if (zoomedState && zoomedScoreMetrics) {
                emitDebugCandidate({
                    phase: 'tighten-zoom',
                    index: stepIndex + 1,
                    total: maxZoomSteps,
                    bearing: zoomedState.bearing,
                    pitch: zoomedState.pitch,
                    requestedZoom: zoomedState.zoom,
                    adaptivePadding,
                    selectionAttempt,
                    selectionTotal,
                    decision: 'accepted',
                    decisionReason: 'zoomed before recenter',
                    ...zoomedScoreMetrics
                });
            }

            let recenteredMetrics;
            let usedRecenterFallback = false;
            try {
                recenteredMetrics = await recenterCameraOnRoute(
                    coords,
                    adaptivePadding,
                    `camera-tighten-center-${stepIndex + 1}`
                );
            } catch (error) {
                console.warn('ActivityMap3D: Failed to recenter tightened camera candidate, scoring zoomed state without recenter.', {
                    stepIndex: stepIndex + 1,
                    selectionAttempt,
                    selectionTotal,
                    error: error?.message || error
                });
                usedRecenterFallback = true;
                recenteredMetrics = getProjectedRouteMetrics(coords, adaptivePadding);
            }
            const candidateState = getCurrentCameraState();
            if (!recenteredMetrics || !candidateState) {
                const fallbackState = getCurrentCameraState();
                if (fallbackState) {
                    recenteredMetrics = getProjectedRouteMetrics(coords, adaptivePadding);
                    usedRecenterFallback = true;
                }
                if (!recenteredMetrics || !fallbackState) {
                    emitDebugCandidate({
                        phase: 'tighten',
                        index: stepIndex + 1,
                        total: maxZoomSteps,
                        bearing: currentState.bearing,
                        pitch: currentState.pitch,
                        requestedZoom: currentState.zoom + zoomStep,
                        adaptivePadding,
                        selectionAttempt,
                        selectionTotal,
                        decision: 'reverted',
                        decisionReason: 'failed to score tightened candidate',
                        ...bestScoreMetrics
                    });
                    await applyDirectCameraState(bestState, `camera-tighten-revert-${stepIndex + 1}`);
                    emitDebugCandidate({
                        phase: 'tighten-revert',
                        index: stepIndex + 1,
                        total: maxZoomSteps,
                        bearing: bestState.bearing,
                        pitch: bestState.pitch,
                        requestedZoom: bestState.zoom,
                        adaptivePadding,
                        selectionAttempt,
                        selectionTotal,
                        decision: 'reverted',
                        decisionReason: 'failed to score tightened candidate',
                        ...bestScoreMetrics
                    });
                    return {
                        cameraState: bestState,
                        fragile: false,
                        score: bestScore,
                        scoreMetrics: bestScoreMetrics,
                        acceptedTightenSteps,
                        fragileReason: ''
                    };
                }
            }
            const resolvedCandidateState = candidateState || getCurrentCameraState();
            if (!resolvedCandidateState || !recenteredMetrics) {
                emitDebugCandidate({
                    phase: 'tighten',
                    index: stepIndex + 1,
                    total: maxZoomSteps,
                    bearing: currentState.bearing,
                    pitch: currentState.pitch,
                    requestedZoom: currentState.zoom + zoomStep,
                    adaptivePadding,
                    selectionAttempt,
                    selectionTotal,
                    decision: 'reverted',
                    decisionReason: 'failed to score tightened candidate',
                    ...bestScoreMetrics
                });
                await applyDirectCameraState(bestState, `camera-tighten-revert-${stepIndex + 1}`);
                emitDebugCandidate({
                    phase: 'tighten-revert',
                    index: stepIndex + 1,
                    total: maxZoomSteps,
                    bearing: bestState.bearing,
                    pitch: bestState.pitch,
                    requestedZoom: bestState.zoom,
                    adaptivePadding,
                    selectionAttempt,
                    selectionTotal,
                    decision: 'reverted',
                    decisionReason: 'failed to score tightened candidate',
                    ...bestScoreMetrics
                });
                return {
                    cameraState: bestState,
                    fragile: false,
                    score: bestScore,
                    scoreMetrics: bestScoreMetrics,
                    acceptedTightenSteps,
                    fragileReason: ''
                };
            }

            const candidateScoreMetrics = scoreCameraCandidate(coords, resolvedCandidateState, adaptivePadding);
            if (!usedRecenterFallback) {
                emitDebugCandidate({
                    phase: 'tighten-center',
                    index: stepIndex + 1,
                    total: maxZoomSteps,
                    bearing: resolvedCandidateState.bearing,
                    pitch: resolvedCandidateState.pitch,
                    requestedZoom: resolvedCandidateState.zoom,
                    adaptivePadding,
                    selectionAttempt,
                    selectionTotal,
                    decision: 'accepted',
                    decisionReason: 'recentered tightened candidate',
                    ...candidateScoreMetrics
                });
            }
            const candidateScore = candidateScoreMetrics.score;
            const candidateOutOfBounds = (candidateScoreMetrics.outsidePaddingAreaRatio ?? 0) > 0;
            const scoreWouldDecrease = !Number.isFinite(candidateScore) || candidateScore < bestScore;
            const shouldStopForBounds = candidateOutOfBounds;
            const shouldRevert = shouldStopForBounds || scoreWouldDecrease;
            const decisionReason = shouldStopForBounds
                ? (usedRecenterFallback ? 'out of bounds (without recenter)' : 'out of bounds')
                : scoreWouldDecrease
                    ? (usedRecenterFallback ? 'score decreased (without recenter)' : 'score decreased')
                    : (usedRecenterFallback ? 'accepted without recenter' : 'accepted');

            if (usedRecenterFallback) {
                emitDebugCandidate({
                    phase: 'tighten',
                    index: stepIndex + 1,
                    total: maxZoomSteps,
                    bearing: resolvedCandidateState.bearing,
                    pitch: resolvedCandidateState.pitch,
                    requestedZoom: resolvedCandidateState.zoom,
                    adaptivePadding,
                    selectionAttempt,
                    selectionTotal,
                    decision: shouldRevert ? 'reverted' : 'accepted',
                    decisionReason,
                    ...candidateScoreMetrics
                });
            }

            if (shouldRevert) {
                await applyDirectCameraState(bestState, `camera-tighten-revert-${stepIndex + 1}`);
                emitDebugCandidate({
                    phase: 'tighten-revert',
                    index: stepIndex + 1,
                    total: maxZoomSteps,
                    bearing: bestState.bearing,
                    pitch: bestState.pitch,
                    requestedZoom: bestState.zoom,
                    adaptivePadding,
                    selectionAttempt,
                    selectionTotal,
                    decision: 'reverted',
                    decisionReason,
                    ...bestScoreMetrics
                });
                return {
                    cameraState: bestState,
                    fragile: false,
                    score: bestScore,
                    scoreMetrics: bestScoreMetrics,
                    acceptedTightenSteps,
                    fragileReason: ''
                };
            }

            bestState = resolvedCandidateState;
            bestMetrics = recenteredMetrics;
            bestScore = candidateScore;
            bestScoreMetrics = candidateScoreMetrics;
            acceptedTightenSteps += 1;
        }

        return {
            cameraState: bestState,
            fragile: false,
            score: bestScore,
            scoreMetrics: bestScoreMetrics,
            acceptedTightenSteps,
            fragileReason: ''
        };
    }, [
        applyDirectCameraState,
        emitDebugCandidate,
        getCurrentCameraState,
        getProjectedRouteMetrics,
        recenterCameraOnRoute,
        scoreCameraCandidate,
        setRenderProgress
    ]);

    const findBestCameraForRoute = useCallback(async (coords, polylineString) => {
        if (!map.current || !coords?.length) return null;

        const cacheKey = getCameraSearchCacheKey(polylineString);
        const cachedCamera = CAMERA_SEARCH_CACHE.get(cacheKey);
        const bounds = getRouteBounds(coords);
        const adaptivePadding = getAdaptivePadding(map.current);

        const debugCandidatesEnabled = enableDebugCandidatesRef.current;
        const debugTransitionDelay = debugTransitionDelayMsRef.current;

        if (cachedCamera && !debugCandidatesEnabled) {
            setRenderProgress('Using cached best 3D angle...', 0.9);
            const appliedCachedCamera = await applyCameraCandidate(bounds, adaptivePadding, cachedCamera, 'cached-camera');
            if (!appliedCachedCamera) {
                console.warn('ActivityMap3D: Cached camera could not be applied, rerunning camera search.');
                CAMERA_SEARCH_CACHE.delete(cacheKey);
            } else {
                const tightenedCachedCamera = await refineCameraFraming(coords, adaptivePadding, {
                    selectionAttempt: 1,
                    selectionTotal: 1
                });
                if (tightenedCachedCamera?.cameraState) {
                    return tightenedCachedCamera.cameraState || getCurrentCameraState() || cachedCamera;
                }

                console.warn('ActivityMap3D: Cached camera could not be refined, rerunning camera search.');
                CAMERA_SEARCH_CACHE.delete(cacheKey);
            }
        } else if (cachedCamera && debugCandidatesEnabled) {
            console.log('ActivityMap3D: Debug candidate capture enabled, bypassing cached best camera.');
        }

        setRenderProgress('Analyzing route layout...', 0.05);
        const principalAxisBearing = calculatePrincipalAxisBearing(coords);
        const initialBearing = calculateStartToEndBearing(coords);
        const coarseCandidates = buildCoarseCandidates([principalAxisBearing, initialBearing], PRIMARY_CAMERA_SEARCH_PITCH);
        const rankedCandidateMap = new Map();
        const refineCandidates = [];
        const upsertRankedCandidate = (cameraCandidate, candidateScore, sourcePhase, cameraState = null) => {
            if (!cameraCandidate || !Number.isFinite(candidateScore?.score)) return;

            const candidateKey = `${normalizeBearing(cameraCandidate.bearing).toFixed(2)}|${cameraCandidate.pitch.toFixed(2)}`;
            const nextCandidate = {
                bearing: normalizeBearing(cameraCandidate.bearing),
                pitch: cameraCandidate.pitch,
                score: candidateScore.score,
                sourcePhase,
                cameraState: cameraState && Array.isArray(cameraState.center) && Number.isFinite(cameraState.zoom)
                    ? {
                        center: [...cameraState.center],
                        zoom: cameraState.zoom,
                        bearing: Number.isFinite(cameraState.bearing) ? cameraState.bearing : normalizeBearing(cameraCandidate.bearing),
                        pitch: Number.isFinite(cameraState.pitch) ? cameraState.pitch : cameraCandidate.pitch
                    }
                    : null
            };
            const existingCandidate = rankedCandidateMap.get(candidateKey);
            if (!existingCandidate || nextCandidate.score > existingCandidate.score) {
                rankedCandidateMap.set(candidateKey, nextCandidate);
            }
        };
        const isBetterRankedCameraCandidate = (nextCandidate, currentBestCandidate) => {
            if (!nextCandidate) return false;
            if (!currentBestCandidate) return true;

            const nextScore = nextCandidate.scoreMetrics?.score ?? Number.NEGATIVE_INFINITY;
            const currentScore = currentBestCandidate.scoreMetrics?.score ?? Number.NEGATIVE_INFINITY;
            if (nextScore !== currentScore) {
                return nextScore > currentScore;
            }

            const nextVisibleCoverage = nextCandidate.scoreMetrics?.visibleSampleRatio ?? Number.NEGATIVE_INFINITY;
            const currentVisibleCoverage = currentBestCandidate.scoreMetrics?.visibleSampleRatio ?? Number.NEGATIVE_INFINITY;
            if (nextVisibleCoverage !== currentVisibleCoverage) {
                return nextVisibleCoverage > currentVisibleCoverage;
            }

            const nextAcceptedSteps = nextCandidate.acceptedTightenSteps ?? -1;
            const currentAcceptedSteps = currentBestCandidate.acceptedTightenSteps ?? -1;
            if (nextAcceptedSteps !== currentAcceptedSteps) {
                return nextAcceptedSteps > currentAcceptedSteps;
            }

            return false;
        };

        let bestCoarseCandidate = {
            bearing: initialBearing,
            pitch: PRIMARY_CAMERA_SEARCH_PITCH,
            score: Number.NEGATIVE_INFINITY,
            visiblePixels: 0,
            onScreenRatio: 0,
            safeRatio: 0
        };
        let bestOverallCandidate = null;
        let coarseSearchLockedZoom = null;

        setProbeVisibility(true);

        for (let i = 0; i < coarseCandidates.length; i += 1) {
            const candidate = coarseCandidates[i];
            const coarseProgress = 0.08 + (((i + 1) / coarseCandidates.length) * 0.58);
            setRenderProgress(`Testing 3D angle ${i + 1}/${coarseCandidates.length}...`, coarseProgress);
            const requestedCamera = await applyCameraCandidate(bounds, adaptivePadding, candidate, `camera-search-${i + 1}`, {
                lockedZoom: coarseSearchLockedZoom
            });
            if (!requestedCamera) continue;
            if (coarseSearchLockedZoom === null && Number.isFinite(requestedCamera.zoom)) {
                coarseSearchLockedZoom = requestedCamera.zoom;
            }

            try {
                await recenterCameraOnRoute(
                    coords,
                    adaptivePadding,
                    `camera-center-initial-coarse-${i + 1}`,
                    { force: true }
                );
            } catch (error) {
                console.warn('ActivityMap3D: Failed to recenter coarse candidate before scoring, keeping current framing.', {
                    bearing: candidate.bearing,
                    pitch: candidate.pitch,
                    error: error?.message || error
                });
            }

            const coarseCameraState = getCurrentCameraState() || requestedCamera;
            const candidateScore = scoreCameraCandidate(
                coords,
                coarseCameraState || candidate,
                adaptivePadding,
                { phase: 'coarse' }
            );
            emitDebugCandidate({
                phase: 'coarse',
                index: i + 1,
                total: coarseCandidates.length,
                bearing: candidate.bearing,
                pitch: candidate.pitch,
                requestedZoom: requestedCamera.zoom,
                adaptivePadding,
                ...candidateScore
            });
            upsertRankedCandidate(candidate, candidateScore, 'coarse', coarseCameraState);
            if (coarseCameraState && isBetterRankedCameraCandidate({
                cameraState: coarseCameraState,
                scoreMetrics: candidateScore,
                acceptedTightenSteps: 0,
                sourcePhase: 'coarse'
            }, bestOverallCandidate)) {
                bestOverallCandidate = {
                    cameraState: coarseCameraState,
                    scoreMetrics: candidateScore,
                    acceptedTightenSteps: 0,
                    sourcePhase: 'coarse'
                };
            }
            if (candidateScore.score > bestCoarseCandidate.score) {
                bestCoarseCandidate = {
                    ...candidate,
                    ...candidateScore,
                    cameraState: coarseCameraState
                };
            }

            if (debugCandidatesEnabled && debugTransitionDelay > 0) {
                await waitForMilliseconds(debugTransitionDelay);
            }
        }

        const rankedCandidates = selectDiverseCameraCandidates(
            Array.from(rankedCandidateMap.values()),
            effectiveRobustRetryLimit
        );
        const selectionSummaries = [];

        if (!rankedCandidates.length) {
            rankedCandidates.push({
                bearing: bestCoarseCandidate.bearing,
                pitch: bestCoarseCandidate.pitch,
                score: bestCoarseCandidate.score,
                sourcePhase: 'fallback',
                cameraState: bestCoarseCandidate.cameraState || null
            });
        }

        let selectedCamera = null;
        let selectedScore = null;

        for (let candidateIndex = 0; candidateIndex < rankedCandidates.length; candidateIndex += 1) {
            const candidate = rankedCandidates[candidateIndex];
            const selectionAttempt = candidateIndex + 1;
            const selectionTotal = rankedCandidates.length;
            try {
                setRenderProgress(`Finalizing 3D angle ${candidateIndex + 1}/${rankedCandidates.length}...`, 0.9);
                let appliedCandidate = null;
                if (candidate.cameraState?.center && Number.isFinite(candidate.cameraState?.zoom)) {
                    const appliedStoredCameraState = await applyDirectCameraState(candidate.cameraState, `best-camera-${candidateIndex + 1}`);
                    if (appliedStoredCameraState) {
                        appliedCandidate = getCurrentCameraState() || candidate.cameraState;
                    } else {
                        console.warn('ActivityMap3D: Failed to restore shortlisted coarse camera state, falling back to refit.', {
                            selectionAttempt,
                            selectionTotal,
                            candidate
                        });
                    }
                }
                if (!appliedCandidate) {
                    appliedCandidate = await applyCameraCandidate(bounds, adaptivePadding, candidate, `best-camera-${candidateIndex + 1}`);
                }
                if (!appliedCandidate) continue;

                const selectionStartState = getCurrentCameraState() || appliedCandidate;
                if (debugCandidatesEnabled && selectionStartState) {
                    const selectionStartScore = scoreCameraCandidate(coords, selectionStartState, adaptivePadding);
                    emitDebugCandidate({
                        phase: 'selection-start',
                        index: selectionAttempt,
                        total: selectionTotal,
                        bearing: selectionStartState.bearing,
                        pitch: selectionStartState.pitch,
                        requestedZoom: selectionStartState.zoom,
                        adaptivePadding,
                        selectionAttempt,
                        selectionTotal,
                        decision: 'accepted',
                        decisionReason: candidate.cameraState?.center && Number.isFinite(candidate.cameraState?.zoom)
                            ? 'restored shortlisted coarse camera'
                            : 'refit shortlisted candidate',
                        sourcePhase: candidate.sourcePhase,
                        ...selectionStartScore
                    });
                }

                const tightenedCandidate = await refineCameraFraming(coords, adaptivePadding, {
                    selectionAttempt,
                    selectionTotal
                });
                const candidateState = tightenedCandidate?.cameraState || getCurrentCameraState() || appliedCandidate;
                if (!candidateState) {
                    console.warn('ActivityMap3D: Skipping selection candidate because no camera state was available after tightening.', {
                        selectionAttempt,
                        selectionTotal,
                        candidate
                    });
                    continue;
                }

                const candidateScore = tightenedCandidate?.scoreMetrics || scoreCameraCandidate(coords, candidateState, adaptivePadding);
                const rankedSelectionCandidate = {
                    cameraState: candidateState,
                    scoreMetrics: candidateScore,
                    acceptedTightenSteps: tightenedCandidate?.acceptedTightenSteps ?? 0,
                    selectionAttempt,
                    selectionTotal,
                    sourcePhase: candidate.sourcePhase,
                    fragile: Boolean(tightenedCandidate?.fragile),
                    fragileReason: tightenedCandidate?.fragileReason || ''
                };
                const isBestSoFar = isBetterRankedCameraCandidate(rankedSelectionCandidate, bestOverallCandidate);

                if (isBestSoFar) {
                    bestOverallCandidate = rankedSelectionCandidate;
                }

                selectionSummaries.push({
                    selectionAttempt,
                    sourcePhase: candidate.sourcePhase,
                    bearing: candidateState.bearing,
                    pitch: candidateState.pitch,
                    zoom: candidateState.zoom,
                    score: candidateScore.score,
                    internalGapCount: candidateScore.internalGapCount,
                    acceptedTightenSteps: tightenedCandidate?.acceptedTightenSteps ?? 0,
                    fragile: Boolean(tightenedCandidate?.fragile),
                    fragileReason: tightenedCandidate?.fragileReason || ''
                });

                emitDebugCandidate({
                    phase: 'selection',
                    index: selectionAttempt,
                    total: selectionTotal,
                    bearing: candidateState.bearing,
                    pitch: candidateState.pitch,
                    requestedZoom: candidateState.zoom,
                    adaptivePadding,
                    selectionAttempt,
                    selectionTotal,
                    decision: isBestSoFar ? 'best-so-far' : 'ranked',
                    decisionReason: isBestSoFar
                        ? 'current highest score across coarse and tighten'
                        : 'did not beat current highest score',
                    sourcePhase: candidate.sourcePhase,
                    acceptedTightenSteps: tightenedCandidate?.acceptedTightenSteps ?? 0,
                    ...candidateScore
                });
            } catch (error) {
                console.warn('ActivityMap3D: Skipping failed selection candidate and continuing to next try.', {
                    selectionAttempt,
                    selectionTotal,
                    candidate,
                    error: error?.message || error
                });
                continue;
            }
        }

        if (!selectedCamera) {
            selectedCamera = bestOverallCandidate?.cameraState || getCurrentCameraState() || {
                bearing: bestCoarseCandidate.bearing,
                pitch: bestCoarseCandidate.pitch
            };
            selectedScore = bestOverallCandidate?.scoreMetrics || scoreCameraCandidate(coords, selectedCamera, adaptivePadding);
        }

        if (selectedCamera?.center && Number.isFinite(selectedCamera?.zoom)) {
            await applyDirectCameraState(selectedCamera, 'selected-camera-final');
            const appliedSelectedCamera = getCurrentCameraState();
            if (appliedSelectedCamera) {
                selectedCamera = appliedSelectedCamera;
                selectedScore = scoreCameraCandidate(coords, selectedCamera, adaptivePadding);
            }
        }

        CAMERA_SEARCH_CACHE.set(cacheKey, {
            bearing: selectedCamera.bearing,
            pitch: selectedCamera.pitch
        });

        console.log('ActivityMap3D: Selected best camera:', selectedCamera);
        emitDebugCandidate({
            phase: 'selected',
            index: 1,
            total: 1,
            bearing: selectedCamera.bearing,
            pitch: selectedCamera.pitch,
            requestedZoom: selectedCamera.zoom,
            adaptivePadding,
            ...selectedScore
        });
        emitDebugSummary({
            enabled: true,
            searchConfig: {
                preferredPitch: PRIMARY_CAMERA_SEARCH_PITCH,
                coarseSearchLockedZoom,
                coarseCenterInitialBeforeScoring: true,
                cameraSearchPitches: getUniqueSearchPitches(PRIMARY_CAMERA_SEARCH_PITCH),
                coarseBearingCount: effectiveCoarseBearingCount,
                coarseBearingStepDegrees: effectiveCoarseBearingStepDegrees,
                refineBearingOffsets: REFINE_BEARING_OFFSETS,
                robustRetryLimit: effectiveRobustRetryLimit,
                tightenZoomStep: TIGHTEN_ZOOM_STEP,
                tightenMaxSteps: TIGHTEN_MAX_STEPS,
                coverageRewardMultiplier: resolveCoverageRewardMultiplier(coverageRewardMultiplier),
                footprintRewardMultiplier: resolveFootprintRewardMultiplier(footprintRewardMultiplier)
            },
            coarseCandidateCount: coarseCandidates.length,
            refineCandidateCount: refineCandidates.length,
            rankedCandidateCount: rankedCandidates.length,
            bestCoarseOrRefineBearing: bestCoarseCandidate.bearing,
            bestCoarseOrRefinePitch: bestCoarseCandidate.pitch,
            selectedCamera,
            selectedScore: selectedScore?.score ?? null,
            visibleSampleRatio: selectedScore?.visibleSampleRatio ?? null,
            internalGapCount: selectedScore?.internalGapCount ?? null,
            attempts: selectionSummaries
        });
        if (!debugCandidatesEnabled) {
            setProbeVisibility(false);
        }
        return selectedCamera;
    }, [
        applyCameraCandidate,
        applyDirectCameraState,
        emitDebugCandidate,
        emitDebugSummary,
        effectiveCoarseBearingCount,
        effectiveCoarseBearingStepDegrees,
        effectiveRobustRetryLimit,
        coverageRewardMultiplier,
        footprintRewardMultiplier,
        getCurrentCameraState,
        buildCoarseCandidates,
        calculateStartToEndBearing,
        calculatePrincipalAxisBearing,
        getAdaptivePadding,
        getCameraSearchCacheKey,
        getRouteBounds,
        recenterCameraOnRoute,
        refineCameraFraming,
        scoreCameraCandidate,
        setProbeVisibility,
        setRenderProgress
    ]);

    useEffect(() => {
        if (!mapContainer.current || !activity) return;

        setIsLoading(true);
        setHasError(false);
        setRenderProgress('Loading 3D terrain...', 0);
        onRenderStartRef.current?.();
        finalRenderResolvedRef.current = false;
        hasBeenInteractedRef.current = false;
        setHasBeenInteracted(false);

        const coordinates = decodePolyline();
        const polylineString = getPolylineString();
        if (!coordinates || coordinates.length === 0) {
            setHasError(true);
            setIsLoading(false);
            setRenderProgress('Failed to load 3D map', null);
            onRenderErrorRef.current?.(new Error('No valid coordinates found for 3D map.'));
            onRenderCompleteRef.current?.();
            return;
        }

        const fallbackBearing = autoBearing ? calculateStartToEndBearing(coordinates) : bearing;

        try {
            const pixelRatio = 1;

            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: mapStyleUrl,
                center: coordinates[Math.floor(coordinates.length / 2)],
                zoom: zoom || 12,
                bearing: fallbackBearing,
                pitch: pitch,
                antialias: true,
                pixelRatio: pixelRatio,
                attributionControl: false,
                interactive: true,
                preserveDrawingBuffer: true
            });

            map.current.on('load', async () => {
                applyReducedLabelDensity(map.current);

                if (!map.current.getSource('mapbox-dem')) {
                    map.current.addSource('mapbox-dem', {
                        type: 'raster-dem',
                        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                        tileSize: 512,
                        maxzoom: 14
                    });
                }

                const styleTerrain = map.current.getStyle?.()?.terrain;
                const currentTerrain = map.current.getTerrain?.();
                if (!(currentTerrain?.source || styleTerrain?.source)) {
                    map.current.setTerrain({
                        source: 'mapbox-dem',
                        exaggeration: DEFAULT_TERRAIN_EXAGGERATION
                    });
                }

                const geoJSON = {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: coordinates
                    }
                };

                map.current.addSource('route', {
                    type: 'geojson',
                    data: geoJSON
                });

                map.current.addLayer({
                    id: ROUTE_LAYER_ID,
                    type: 'line',
                    source: 'route',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round'
                    },
                    paint: {
                        'line-color': '#FC4C02',
                        'line-width': normalizeRouteLineWidth(lineWidth),
                        'line-opacity': ROUTE_LINE_OPACITY
                    }
                });

                map.current.addLayer({
                    id: VISIBILITY_PROBE_LAYER_ID,
                    type: 'line',
                    source: 'route',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                        visibility: 'none'
                    },
                    paint: {
                        'line-color': `rgb(${VISIBILITY_PROBE_COLOR.r}, ${VISIBILITY_PROBE_COLOR.g}, ${VISIBILITY_PROBE_COLOR.b})`,
                        'line-width': normalizeRouteLineWidth(lineWidth + 8),
                        'line-opacity': 1
                    }
                });

                const fitBoundsToRoute = async () => {
                    if (!map.current) return;

                    try {
                        const bounds = getRouteBounds(coordinates);
                        const adaptivePadding = getAdaptivePadding(map.current);
                        const initialBearing = autoBearing ? calculateStartToEndBearing(coordinates) : bearing;

                        if (autoBearing) {
                            setRenderProgress('Preparing baseline 3D view...', 0.02);
                            const initialCamera = await applyInitialMainCamera(bounds, adaptivePadding, initialBearing, PRIMARY_CAMERA_SEARCH_PITCH, 'initial-main-camera');

            const debugTransitionDelay = debugTransitionDelayMsRef.current;
            if (enableDebugCandidatesRef.current && debugTransitionDelay > 0) {
                await waitForMilliseconds(debugTransitionDelay);
            }

                            if (enableCameraSearch) {
                                await findBestCameraForRoute(coordinates, polylineString);
                            } else {
                                emitDebugSummary({
                                    enabled: false,
                                    selectedCamera: initialCamera || getCurrentCameraState(),
                                    searchConfig: {
                                        preferredPitch: PRIMARY_CAMERA_SEARCH_PITCH,
                                        cameraSearchDisabled: true
                                    }
                                });
                            }
                        } else {
                            setRenderProgress('Finalizing 3D camera...', 0.95);
                            await applyCameraCandidate(bounds, adaptivePadding, {
                                bearing,
                                pitch
                            }, 'manual-camera');
                            emitDebugSummary({
                                enabled: false,
                                selectedCamera: getCurrentCameraState()
                            });
                        }
                    } catch (error) {
                        console.error('ActivityMap3D: Error fitting bounds:', error);

                        const centerCoord = coordinates[Math.floor(coordinates.length / 2)];
                        if (centerCoord && !isNaN(centerCoord[0]) && !isNaN(centerCoord[1])) {
                            map.current.jumpTo({
                                center: centerCoord,
                                zoom: zoom || 13,
                                bearing: fallbackBearing,
                                pitch: pitch
                            });
                        }
                    }
                    if (!enableDebugCandidatesRef.current) {
                        setProbeVisibility(false);
                    }
                    setRenderProgress('3D map ready', 1);
                    setIsLoading(false);
                    scheduleFinalRenderCheck();
                };

                map.current.once('idle', () => {
                    void fitBoundsToRoute();
                });
            });

            const updateCamera = () => {
                if (!map.current) return;
            };

            const handleInteraction = () => {
                if (suppressInteractionRef.current) return;
                if (!hasBeenInteractedRef.current) {
                    hasBeenInteractedRef.current = true;
                    setHasBeenInteracted(true);
                }
                updateCamera();
                if (mapInteractionCallbackRef.current) {
                    mapInteractionCallbackRef.current(() => captureCallbackRef.current());
                }
            };

            map.current.on('move', handleInteraction);
            map.current.on('moveend', handleInteraction);

            map.current.on('error', (e) => {
                console.error('Map error:', e);
                setHasError(true);
                setIsLoading(false);
                setRenderProgress('Failed to load 3D map', null);
                onRenderErrorRef.current?.(e?.error || new Error('Failed to load 3D map'));
                onRenderCompleteRef.current?.();
            });

        } catch (error) {
            console.error('Error initializing map:', error);
            setHasError(true);
            setIsLoading(false);
            setRenderProgress('Failed to load 3D map', null);
            onRenderErrorRef.current?.(error instanceof Error ? error : new Error('Failed to initialize 3D map'));
            onRenderCompleteRef.current?.();
        }

        return () => {
            onProgressChangeRef.current?.('');
            onProgressMetricsRef.current?.(null);
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [
        activity,
        selectedSegment,
        mapStyleUrl,
        autoBearing,
        bearing,
        pitch,
        zoom,
        lineWidth,
        enableCameraSearch,
        highDPI,
        decodePolyline,
        getPolylineString,
        scheduleFinalRenderCheck,
        calculateStartToEndBearing,
        getAdaptivePadding,
        getRouteBounds,
        findBestCameraForRoute,
        applyCameraCandidate,
        applyInitialMainCamera,
        getCurrentCameraState,
        emitDebugSummary,
        setProbeVisibility,
        setRenderProgress
    ]);

    if (!activity) {
        return (
            <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f0e8', borderRadius: '8px' }}>
                <span style={{ color: '#666' }}>No activity selected</span>
            </div>
        );
    }

    if (hasError) {
        return (
            <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff3cd', borderRadius: '8px' }}>
                <span style={{ color: '#856404' }}>Error loading 3D map</span>
            </div>
        );
    }

    // Use display dimensions if provided, otherwise use render dimensions
    const containerWidth = displayWidth || width;
    const containerHeight = displayHeight || height;

    return (
        <div style={{
            position: 'relative',
            width: containerWidth,
            height: containerHeight,
            overflow: 'hidden'
        }}>
            {/* Main map container - render at full resolution but scale down visually */}
            <div style={{
                position: 'relative',
                width: width,
                height: height,
                transform: displayWidth ? `scale(${containerWidth / width})` : undefined,
                transformOrigin: 'top left',
                borderRadius: '8px',
                overflow: 'hidden'
            }}>
                <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />

                {showLoadingOverlay && isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ marginBottom: '10px' }}>{progressText || 'Loading 3D map...'}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                                {activity.name || 'Activity'}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ActivityMap3D;
